import { createShadowRoot } from "@sinch/mfe-helper-react";
import "core-js";
import { Provider } from "jotai/react";
import React, { Suspense } from "react";
import { render } from "react-dom";
import { StyleSheetManager } from "styled-components";
import { ConditionalWrapper } from "./components/ConditionalWrapper";
import { ErrorBoundary } from "./components/ErrorBoundary";
import type { Config } from "./config/client";
import { setupNectary } from "./nectary-setup";

export const APP_NAME = "Shell";

async function getShellRenderTarget(
  withoutShadowRoot: boolean
): Promise<[HTMLDivElement, ShadowRoot | undefined]> {
  const customElementRegistry = await setupNectary(withoutShadowRoot);
  window.shellCustomRegistry = customElementRegistry;
  const shellNode = document.querySelector<HTMLDivElement>("#shell");

  if (!shellNode) {
    throw new Error("#shell not found!");
  }

  if (withoutShadowRoot) {
    return [shellNode, undefined];
  }

  const { shadowRoot, appElement } = createShadowRoot(
    shellNode,
    customElementRegistry,
    APP_NAME
  );
  // Some MFEs uses library (e.g. Material UI) that needs this to render modals properly
  Object.defineProperty(shadowRoot, "defaultView", { value: window });

  appElement.style.height = "100%";

  return [appElement, shadowRoot];
}

export interface RenderShellOpts {
  app: React.ComponentType;
}

const getThemeClassNames = (
  portalExtraNectaryTheme: string | null,
  vendor?: string | null
) => {
  if (vendor === "simpletexting") {
    return "nectary-theme-base nectary-theme-simple-texting";
  }

  const extraClassName = portalExtraNectaryTheme
    ? `nectary-theme-${portalExtraNectaryTheme}`
    : "";

  return `nectary-theme-base ${extraClassName}`;
};

/** Needs to be called after the shadow root was created */
const loadThemes = async (classNames: string) => {
  await import("@nectary/theme-base");
  if (classNames.includes("message-media")) {
    await import("@nectary/theme-message-media");
  }
  if (classNames.includes("simple-texting")) {
    await import("@nectary/theme-simple-texting");
  }
};

export async function renderShell(config: Config, { app }: RenderShellOpts) {
  const { extraNectaryTheme, withoutShadowRoot } =
    config.universalBootstrapOptions;
  const [renderTarget, shadowRoot] = await getShellRenderTarget(
    withoutShadowRoot
  );
  const { globalJotaiStore } = await import("./jotai-store");

  const classNames = getThemeClassNames(extraNectaryTheme, config.VENDOR_LABEL);
  await loadThemes(classNames);

  renderTarget.className = classNames;

  const App = app;
  render(
    <ConditionalWrapper
      shouldWrap={!!shadowRoot}
      wrapper={(children) => (
        <StyleSheetManager target={shadowRoot}>{children}</StyleSheetManager>
      )}
    >
      <ErrorBoundary>
        <Provider store={globalJotaiStore}>
          <Suspense fallback={null}>
            <App />
          </Suspense>
        </Provider>
      </ErrorBoundary>
    </ConditionalWrapper>,
    renderTarget
  );
}
